// ProfileLandingPanel — U4 (2026-07-09). The Profile tab's landing page.
//
// Replaces the old 4-tab bar (वॉलेट / जमा 🫙 / Rates / खाता) with a plain
// card MENU: five cards, each carrying its key number and one plain-words
// sentence, each opening its own full page. Hub-and-spoke instead of tabs —
// a first-time broker reads the card, sees the number, taps for detail.
//
//   My wallet          → /profile/wallet   (balance + requests + history)
//   Security deposit   → /profile/deposit  (single home of the deposit now)
//   My rates           → /profile/rates
//   Rewards & referrals→ /profile/rewards  (folded from the old /rewards tab)
//   Account & security → /profile/account
//
// A rose danger banner sits on top when the agent's own money needs
// attention — the same signal as the bottom-nav danger dot, explained in
// plain words right where the agent lands.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { T } from '@baaligames/shared-ui/admin/lib/textStyles';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { useAgentWallet } from '@baaligames/shared-ui/admin/lib/agentWallet';
import { useT } from '@baaligames/shared-ui/lib/i18n';

const inr = (n: number) => `₹${Math.abs(n).toLocaleString('en-IN')}`;

function MenuCard({
  to, icon, tone, title, sub, right,
}: {
  to: string; icon: string; tone: string; title: string; sub: string; right?: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-2xl p-4
                 bg-white dark:bg-arena-700/40
                 border border-arena-200/60 dark:border-arena-600/40
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                 hover:ring-2 hover:ring-brand-400/40 transition"
    >
      <span className={`grid place-items-center w-11 h-11 rounded-xl shrink-0 ring-1 ${tone}`}>
        <Icon name={icon} size={22} filled />
      </span>
      <div className="min-w-0 flex-1">
        <div className={`text-[15px] font-bold ${T.primary}`}>{title}</div>
        <div className={`text-[12px] mt-0.5 ${T.tertiary}`}>{sub}</div>
      </div>
      {right}
      <Icon name="chevron_right" size={20} className="text-arena-400 dark:text-neon-200/60 shrink-0" />
    </Link>
  );
}

export default function ProfileLandingPanel() {
  const t = useT();
  const { admin } = useAdminAuth();
  const { balance } = useAgentWallet();
  const depositBalance = Number((admin as any)?.depositBalance ?? 0);
  const walletNegative = balance < 0;
  const depositZero = depositBalance === 0;

  // New (not-yet-joined) offers — drives the little count chip on the
  // Rewards card, same signal the bottom-nav Profile badge carries.
  const [newOffers, setNewOffers] = useState(0);
  useEffect(() => {
    let alive = true;
    adminApi.get<{ offers: Array<{ am_opted_in: 0 | 1 | boolean }> }>('/admin/me-agent/offers')
      .then((r) => { if (alive) setNewOffers((r.offers || []).filter((o) => !o.am_opted_in).length); })
      .catch(() => { /* card still renders without the chip */ });
    return () => { alive = false; };
  }, []);

  const depositStatus = depositZero
    ? { label: t('agent.profile.landing.deposit.zero'), cls: 'text-amber-700 dark:text-amber-300' }
    : { label: t('agent.profile.landing.deposit.safe'), cls: 'text-emerald-700 dark:text-emerald-300' };

  return (
    <div className="space-y-3">
      {/* Danger banner — plain words for the bottom-nav danger dot. */}
      {(walletNegative || depositZero) && (
        <div className="rounded-2xl border-2 border-rose-400 dark:border-rose-500/50
                        bg-rose-50 dark:bg-rose-950/25 p-3.5 flex items-start gap-2.5">
          <span className="grid place-items-center w-8 h-8 rounded-xl shrink-0
                           bg-rose-500/20 text-rose-700 dark:text-rose-300">
            <Icon name="priority_high" size={18} filled />
          </span>
          <div className="text-[13px] leading-snug text-rose-900 dark:text-rose-100 font-semibold pt-1">
            {walletNegative ? t('agent.profile.landing.danger.wallet') : t('agent.profile.landing.danger.deposit')}
          </div>
        </div>
      )}

      <MenuCard
        to="/profile/wallet" icon="account_balance_wallet"
        tone="bg-brand-50 text-brand-700 ring-brand-100 dark:bg-brand-400/15 dark:text-brand-300 dark:ring-brand-400/30"
        title={t('agent.profile.landing.wallet.title')}
        sub={t('agent.profile.landing.wallet.sub')}
        right={
          <span className={`shrink-0 font-black text-[17px] tabular-nums ${
            walletNegative ? 'text-rose-600 dark:text-rose-300' : T.primary}`}>
            {walletNegative ? `−${inr(balance)}` : inr(balance)}
          </span>
        }
      />

      <MenuCard
        to="/profile/deposit" icon="account_balance"
        tone="bg-indigo-50 text-indigo-700 ring-indigo-100 dark:bg-indigo-400/15 dark:text-indigo-300 dark:ring-indigo-400/30"
        title={t('agent.profile.landing.deposit.title')}
        sub={t('agent.profile.landing.deposit.sub')}
        right={
          <span className="shrink-0 text-right">
            <span className={`block font-black text-[17px] tabular-nums ${T.primary}`}>{inr(depositBalance)}</span>
            <span className={`block text-[10.5px] font-bold ${depositStatus.cls}`}>{depositStatus.label}</span>
          </span>
        }
      />

      <MenuCard
        to="/profile/rates" icon="percent"
        tone="bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30"
        title={t('agent.profile.landing.rates.title')}
        sub={t('agent.profile.landing.rates.sub')}
      />

      <MenuCard
        to="/profile/rewards" icon="card_giftcard"
        tone="bg-amber-50 text-amber-700 ring-amber-100 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30"
        title={t('agent.profile.landing.rewards.title')}
        sub={t('agent.profile.landing.rewards.sub')}
        right={newOffers > 0 ? (
          <span className="shrink-0 inline-flex items-center justify-center h-[22px] min-w-[22px] px-1.5
                           rounded-full text-[11px] font-extrabold tabular-nums
                           bg-brand-500 text-white shadow-[0_1px_4px_rgba(233,30,140,0.55)]">
            {newOffers}
          </span>
        ) : undefined}
      />

      <MenuCard
        to="/profile/account" icon="badge"
        tone="bg-sky-50 text-sky-700 ring-sky-100 dark:bg-sky-400/15 dark:text-sky-300 dark:ring-sky-400/30"
        title={t('agent.profile.landing.account.title')}
        sub={t('agent.profile.landing.account.sub')}
      />
    </div>
  );
}
